<template>
  <div class="login-page">
    <!-- Gradient background with noise texture -->
    <div class="login-bg">
      <div class="bg-noise" />
    </div>

    <!-- Language selector -->
    <div class="login-locale">
      <locale />
    </div>

    <!-- Centered login container -->
    <div class="login-container">
      <!-- Logo -->
      <div class="login-logo">
        <logo-full />
      </div>

      <!-- Login card with sliding panels -->
      <div class="login-card">
        <div class="card-slider">
          <div
            class="card-track"
            :style="{ transform: showForgotPassword ? 'translateX(-50%)' : 'translateX(0)' }"
          >
            <!-- Panel 1: Login form -->
            <div class="slide-panel">
              <div class="card-header-section">
                <h1 class="login-title">
                  {{ $t('DEBTIA_LOGIN_TITLE') }}
                </h1>
                <p class="login-subtitle">
                  {{ $t('DEBTIA_LOGIN_SUBTITLE') }}
                </p>
              </div>

              <!-- Error alert -->
              <b-alert
                :show="errorMessages.length > 0"
                variant="danger"
                class="login-alert"
              >
                <div class="alert-body">
                  <feather-icon
                    icon="AlertCircleIcon"
                    class="alert-icon"
                  />
                  <div class="alert-text">
                    <span
                      v-for="(message, index) in errorMessages"
                      :key="index"
                    >{{ message }}</span>
                  </div>
                </div>
              </b-alert>

              <!-- Login form -->
              <validation-observer ref="loginForm">
                <b-form
                  class="login-form"
                  @submit.prevent="login"
                >
                  <!-- Username -->
                  <div class="form-field">
                    <label for="login-username">{{ $t('DEBTIA_USERNAME_LABEL') }}</label>
                    <validation-provider
                      #default="{ errors }"
                      name="Username"
                      rules="required"
                    >
                      <div class="input-wrapper">
                        <feather-icon
                          icon="UserIcon"
                          class="field-icon"
                        />
                        <b-form-input
                          id="login-username"
                          v-model="username"
                          :state="errors.length > 0 ? false : null"
                          name="login-username"
                          :placeholder="$t('DEBTIA_USERNAME_PLACEHOLDER')"
                          class="login-input"
                        />
                      </div>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </div>

                  <!-- Password -->
                  <div class="form-field">
                    <label for="login-password">{{ $t('DEBTIA_PASSWORD_LABEL') }}</label>
                    <validation-provider
                      #default="{ errors }"
                      name="Password"
                      rules="required"
                    >
                      <div class="input-wrapper">
                        <feather-icon
                          icon="LockIcon"
                          class="field-icon"
                        />
                        <b-form-input
                          id="login-password"
                          v-model="password"
                          :state="errors.length > 0 ? false : null"
                          class="login-input password-input"
                          :type="passwordFieldType"
                          name="login-password"
                          :placeholder="$t('DEBTIA_PASSWORD_PLACEHOLDER')"
                        />
                        <feather-icon
                          class="toggle-password"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </div>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <a
                      href="#"
                      class="forgot-link"
                      @click.prevent="showForgot"
                    >
                      {{ $t('DEBTIA_FORGOT_PASSWORD') }}
                    </a>
                  </div>

                  <!-- Submit -->
                  <b-button
                    type="submit"
                    variant="primary"
                    block
                    class="login-btn"
                    :disabled="loginLoading"
                  >
                    <b-spinner
                      v-if="loginLoading"
                      small
                      class="mr-50"
                    />
                    {{ loginLoading ? $t('DEBTIA_LOGGING_IN') : $t('DEBTIA_LOGIN_BUTTON') }}
                  </b-button>
                </b-form>
              </validation-observer>
            </div>

            <!-- Panel 2: Forgot password -->
            <div class="slide-panel">
              <div class="card-header-section">
                  <a
                    href="#"
                    class="back-link"
                    @click.prevent="goBackToLogin"
                  >
                    <feather-icon
                      icon="ArrowLeftIcon"
                      size="16"
                    />
                    {{ $t('DEBTIA_BACK_TO_LOGIN') }}
                  </a>
                  <h1 class="login-title">
                    {{ $t('DEBTIA_FORGOT_PASSWORD') }}
                  </h1>
                  <p class="login-subtitle">
                    {{ $t('DEBTIA_FORGOT_PASSWORD_TEXT') }}
                  </p>
                </div>

                <validation-observer ref="simpleRules">
                  <b-form class="login-form">
                    <div class="form-field">
                      <label>{{ $t('DEBTIA_USERNAME_LABEL') }}</label>
                      <validation-provider
                        #default="{ errors }"
                        name="Username"
                        rules="required"
                      >
                        <div class="input-wrapper">
                          <feather-icon
                            icon="UserIcon"
                            class="field-icon"
                          />
                          <b-form-input
                            v-model="forgotPasswordUsername"
                            :state="errors.length > 0 ? false : null"
                            :placeholder="$t('DEBTIA_USERNAME_PLACEHOLDER')"
                            class="login-input"
                          />
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>
                    <b-button
                      variant="primary"
                      block
                      class="login-btn"
                      @click.prevent="validationForm"
                    >
                      {{ $t('DEBTIA_SEND_RESET_LINK') }}
                    </b-button>
                  </b-form>
                </validation-observer>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="login-footer">
        <div class="footer-badge">
          <feather-icon
            icon="ShieldIcon"
            size="12"
          />
          {{ $t('DEBTIA_SECURE_CONNECTION') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import LogoFull from '@core/layouts/components/LogoFull.vue'
import {
  BFormInput, BForm, BButton, BAlert, BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import locale from '@core/layouts/components/app-navbar/components/Locale.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/auth/jwt/useJwt'
import axios from '@axios'
import i18n from '@/libs/i18n'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'

export default {
  components: {
    BFormInput,
    BForm,
    BButton,
    BAlert,
    BSpinner,
    LogoFull,
    ValidationProvider,
    ValidationObserver,
    locale,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      password: '',
      username: '',
      required,
      error: [],
      forgotPasswordUsername: '',
      forgetpasswordloader: false,
      loginLoading: false,
      showForgotPassword: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    // API'et svarer enten med en enkelt streng, en liste eller et objekt med
    // felt-fejl. Uden normalisering her ville v-for løbe hen over en streng
    // tegn for tegn og lave ét span per bogstav.
    errorMessages() {
      if (!this.error) return []
      if (Array.isArray(this.error)) return this.error
      if (typeof this.error === 'object') return Object.values(this.error).flat()
      return [this.error]
    },
  },
  watch: {
    forgetpasswordloader(val) {
      this.$store.commit('app/SET_GLOBAL_LOADING', { show: val, text: this.$t('DEBTIA_LOGGING_IN') })
    },
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  created() {
    if (localStorage.getItem('accessToken')) {
      this.$router.replace('/')
    }
  },
  methods: {
    showForgot() {
      this.showForgotPassword = true
    },
    goBackToLogin() {
      this.showForgotPassword = false
      this.forgotPasswordUsername = ''
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          this.forgetpasswordloader = true
          const url = '/generate-forgot-password-link'
          axios.post(url, {
            username: this.forgotPasswordUsername,
          }).then(response => {
            if (response.status === 200) {
              this.forgetpasswordloader = false
              this.forgotPasswordUsername = ''
              this.showForgotPassword = false
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Success',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: this.$t('DEBTIA_RESET_EMAIL_SENT'),
                },
              })
            }
          }).catch(error => {
            const noinputmsg = error.response.data.message ? error.response.data.message : error.message
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_ERROR'),
                icon: 'CoffeeIcon',
                variant: 'danger',
                text: noinputmsg,
              },
            })
            this.forgetpasswordloader = false
          })
        }
      })
    },
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.loginLoading = true
          useJwt.login({
            username: this.username,
            password: this.password,
          })
            .then(response => {
              const userData = response.data
              useJwt.setToken(userData.token)
              localStorage.setItem(jwtDefaultConfig.storageUserData, JSON.stringify(userData))
              this.$ability.update(userData.ability)
              this.fetchTranslations()
              axios.get('/user-settings/get?key=debtors_own_by')
                .then(debtorresponse => {
                  if (debtorresponse.status) {
                    localStorage.setItem('debtorsBelongTo', debtorresponse.data.data.value)
                    this.$router.replace('/')
                      .then(() => {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: `${this.$t('DEBTIA_WELCOME')} ${userData.data.name || userData.data.username}`,
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: this.$t('DEBTIA_LOGGED_IN'),
                          },
                        })
                      })
                  }
                })
            })
            .catch(error => {
              this.error = error.response.data.message
              this.$refs.loginForm.setErrors(error.response.data.message)
            })
            .finally(() => {
              this.loginLoading = false
            })
        }
      })
    },
    async fetchTranslations() {
      const url = '/translations/get-user-translations'
      const response = await axios.get(url)
      for (const [lang, trans] of Object.entries(response.data.data)) {
        i18n.setLocaleMessage(lang, trans)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// ============================
// Keyframe animations
// ============================
@keyframes card-enter {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes logo-enter {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ============================
// Layout
// ============================
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  font-size: 0.8rem;
}

// ============================
// Background - Debtia gradient
// ============================
.login-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background:
    radial-gradient(ellipse 80% 50% at 20% 20%, rgba(8, 140, 118, 0.18) 0%, transparent 50%),
    radial-gradient(ellipse 60% 80% at 80% 30%, rgba(0, 41, 84, 0.12) 0%, transparent 50%),
    radial-gradient(ellipse 70% 60% at 60% 80%, rgba(8, 140, 118, 0.14) 0%, transparent 50%),
    radial-gradient(ellipse 50% 50% at 10% 70%, rgba(12, 56, 96, 0.08) 0%, transparent 50%),
    #f5f7fa;

  .bg-noise {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'><defs><filter id='n' x='0' y='0' width='100%' height='100%' color-interpolation-filters='sRGB'><feTurbulence type='fractalNoise' baseFrequency='0.3' numOctaves='3' stitchTiles='stitch' result='t'/><feColorMatrix type='saturate' values='0' in='t' result='g'/><feComponentTransfer in='g' result='a'><feFuncA type='linear' slope='0.5'/></feComponentTransfer></filter></defs><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
    background-size: 200px;
    opacity: 0.12;
    pointer-events: none;
  }
}

// ============================
// Language
// ============================
.login-locale {
  position: fixed;
  top: 1.25rem;
  right: 1.5rem;
  z-index: 10;

  // <locale> er en b-nav-item-dropdown og udsender derfor et <li>. Inde i
  // navbaren ligger den i <ul class="navbar-nav">, som nulstiller list-style.
  // Her står den frit, så browserens standard-bullet slår igennem.
  ::v-deep li {
    list-style: none;
  }
}

// ============================
// Container & Logo
// ============================
.login-container {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 480px;
  padding: 2rem;
}

.login-logo {
  margin-bottom: 2.5rem;
  animation: logo-enter 0.5s ease-out;

  ::v-deep .debtia-logo {
    height: 46px;
    width: auto;
  }

  ::v-deep .debtia-logo-dark {
    fill: #002954;
  }

  ::v-deep .debtia-logo-accent {
    fill: #088c76;
  }
}

// ============================
// Card
// ============================
.login-card {
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.02),
    0 2px 4px rgba(0, 0, 0, 0.02),
    0 8px 16px rgba(0, 0, 0, 0.04),
    0 24px 48px rgba(0, 0, 0, 0.06);
  animation: card-enter 0.5s ease-out 0.1s both;
  overflow: hidden;
}

// ============================
// Slider mechanics
// ============================
.card-slider {
  overflow: hidden;
}

.card-track {
  display: flex;
  width: 200%;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-panel {
  width: 50%;
  flex-shrink: 0;
  padding: 2.5rem;
}

// ============================
// Header
// ============================
.card-header-section {
  margin-bottom: 1.75rem;
}

.login-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0a2540;
  margin-bottom: 0.35rem;
  letter-spacing: -0.025em;
}

.login-subtitle {
  font-size: 0.85rem;
  color: #687385;
  margin-bottom: 0;
  line-height: 1.5;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 400;
  color: #088c76;
  text-decoration: none;
  margin-bottom: 1.25rem;
  transition: color 0.15s ease;

  &:hover {
    color: #066b5a;
  }
}

// ============================
// Alert
// ============================
.login-alert {
  background: rgba(234, 84, 85, 0.06) !important;
  border: 1px solid rgba(234, 84, 85, 0.15) !important;
  color: #d63031 !important;
  border-radius: 10px;
  font-size: 0.72rem;
  line-height: 1.45;
  padding: 0.5rem 0.65rem;
  margin-bottom: 1.5rem;

  .alert-body {
    display: flex;
    // Flere linjer tekst: ikonet skal stå ud for den første linje,
    // ikke midt i blokken.
    align-items: flex-start;
    padding: 0;
  }

  // Feather-ikoner har en fast px-størrelse, mens teksten skalerer med rem.
  // em binder ikonet til tekststørrelsen, så de følges ad.
  .alert-icon {
    width: 1.05em;
    height: 1.05em;
    flex-shrink: 0;
    margin-top: 0.2em;
    margin-right: 0.5em;
  }

  .alert-text {
    display: flex;
    flex-direction: column;
    gap: 0.15em;
  }
}

// ============================
// Form
// ============================
.login-form {
  .form-field {
    margin-bottom: 1.25rem;

    label {
      display: block;
      font-size: 0.78rem;
      font-weight: 700;
      color: #3c4257;
      margin-bottom: 0.5rem;
      letter-spacing: 0.01em;
    }

    .input-wrapper {
      position: relative;

      .field-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #9ca3b4;
        width: 16px;
        height: 16px;
        z-index: 2;
        pointer-events: none;
      }

      .toggle-password {
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #9ca3b4;
        width: 16px;
        height: 16px;
        cursor: pointer;
        z-index: 2;
        transition: color 0.15s ease;

        &:hover {
          color: #088c76;
        }
      }

      .login-input {
        background: #ffffff !important;
        border: 1px solid #d8dce6 !important;
        border-radius: 10px;
        color: #0a2540 !important;
        padding: 0.7rem 1rem 0.7rem 2.75rem;
        font-size: 0.85rem;
        height: auto;
        transition: all 0.15s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

        &::placeholder {
          color: #a3acb9;
          font-size: 0.85rem;
        }

        &:hover {
          border-color: #c1c7d4 !important;
        }

        &:focus {
          background: #ffffff !important;
          border-color: #088c76 !important;
          box-shadow: 0 0 0 3px rgba(8, 140, 118, 0.12), 0 1px 2px rgba(0, 0, 0, 0.04);
          outline: none;
        }
      }

      .password-input {
        padding-right: 2.75rem;
      }
    }

    .forgot-link {
      display: inline-block;
      margin-top: 0.5rem;
      font-size: 0.78rem;
      font-weight: 400;
      color: #088c76;
      text-decoration: none;
      transition: color 0.15s ease;

      &:hover {
        color: #066b5a;
      }
    }

    .text-danger {
      font-size: 0.75rem;
      margin-top: 0.35rem;
      display: block;
    }
  }
}

// ============================
// Button
// ============================
.login-btn {
  margin-top: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 10px;
  background: #088c76 !important;
  border: none !important;
  letter-spacing: 0.01em;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px rgba(8, 140, 118, 0.2), 0 4px 12px rgba(8, 140, 118, 0.15);

  &:hover:not(:disabled) {
    background: #079480 !important;
    box-shadow: 0 2px 6px rgba(8, 140, 118, 0.25), 0 8px 20px rgba(8, 140, 118, 0.2);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    background: #067564 !important;
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(8, 140, 118, 0.2);
  }
}

// ============================
// Footer
// ============================
.login-footer {
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: card-enter 0.5s ease-out 0.25s both;

  .footer-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 400;
    color: #687385;
    padding: 0.35rem 0.85rem;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 0, 0, 0.05);
  }
}

// ============================
// Responsive
// ============================
@media (max-width: 520px) {
  .login-container {
    padding: 1.25rem;
    max-width: 100%;
  }

  .slide-panel {
    padding: 2rem 1.5rem;
  }

  .login-title {
    font-size: 1.2rem;
  }
}
</style>
